import { prefersReducedMotion } from '@theme/utilities';

class StatsBlock extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    this.#setupObserver();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #setupObserver() {
    const values = this.querySelectorAll('[data-stats-value]');
    if (!values.length) return;

    const duration = (parseFloat(this.dataset.animationSpeed) || 2) * 1000;

    this.#observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const el = entry.target;
          this.#observer?.unobserve(el);
          this.#animateValue(/** @type {HTMLElement} */ (el), duration);
        });
      },
      { threshold: 0.25 }
    );

    values.forEach((el) => this.#observer?.observe(el));
  }

  /**
   * @param {HTMLElement} el
   * @param {number} duration
   */
  #animateValue(el, duration) {
    if (el.dataset.animated === 'true') return;
    el.dataset.animated = 'true';

    const target = parseFloat(el.dataset.statsValue) || 0;

    if (prefersReducedMotion()) {
      el.textContent = `${Math.round(target)}%`;
      return;
    }

    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(target * eased);
      el.textContent = `${current}%`;

      if (progress < 1) {
        requestAnimationFrame(tick);
      } else {
        el.textContent = `${Math.round(target)}%`;
      }
    };

    requestAnimationFrame(tick);
  }
}

if (!customElements.get('stats-block')) {
  customElements.define('stats-block', StatsBlock);
}
